import { useState, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import type { CourseFilters, CourseStatus, CourseMode } from "../types"

interface CourseFiltersBarProps {
  filters: CourseFilters
  onChange: (filters: CourseFilters) => void
}

export function CourseFiltersBar({ filters, onChange }: CourseFiltersBarProps) {
  const { t } = useTranslation()
  const [searchDraft, setSearchDraft] = useState(filters.search ?? "")
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    const timeout = setTimeout(() => {
      onChange({ ...filters, search: searchDraft || undefined, page: 1 })
    }, 300)
    return () => clearTimeout(timeout)
  }, [searchDraft]) // eslint-disable-line react-hooks/exhaustive-deps

  const hasActiveFilters = !!(filters.status || filters.search || filters.subject || filters.mode)

  function resetFilters() {
    setSearchDraft("")
    onChange({ page: 1 })
  }

  return (
    <div
      className="flex flex-wrap items-center gap-3"
      role="search"
      aria-label={t("courses.filters_label")}
    >
      {/* Search input */}
      <div className="relative min-w-52 flex-1">
        <label htmlFor="course-search" className="sr-only">
          {t("courses.search_label")}
        </label>
        <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
            className="text-neutral-600"
          >
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>
        <input
          id="course-search"
          type="search"
          placeholder={t("courses.search_placeholder")}
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          className="w-full rounded-lg border border-surface-200 bg-white py-2.5 pl-9 pr-3 text-sm text-neutral-900 placeholder:text-neutral-600 focus:border-mindeia-primary focus:outline-none focus:ring-1 focus:ring-mindeia-primary"
          style={{ minHeight: "44px" }}
        />
      </div>

      {/* Status filter */}
      <div>
        <label htmlFor="course-status-filter" className="sr-only">
          {t("courses.filter_by_status")}
        </label>
        <select
          id="course-status-filter"
          value={filters.status ?? ""}
          onChange={(e) =>
            onChange({
              ...filters,
              status: (e.target.value as CourseStatus) || undefined,
              page: 1,
            })
          }
          className="rounded-lg border border-surface-200 bg-white px-3 py-2.5 text-sm text-neutral-700 focus:border-mindeia-primary focus:outline-none focus:ring-1 focus:ring-mindeia-primary"
          style={{ minHeight: "44px" }}
        >
          <option value="">{t("courses.all_statuses")}</option>
          <option value="draft">{t("courses.status.draft")}</option>
          <option value="active">{t("courses.status.active")}</option>
          <option value="archived">{t("courses.status.archived")}</option>
        </select>
      </div>

      {/* Mode filter */}
      <div>
        <label htmlFor="course-mode-filter" className="sr-only">
          {t("courses.filter_by_mode")}
        </label>
        <select
          id="course-mode-filter"
          value={filters.mode ?? ""}
          onChange={(e) =>
            onChange({
              ...filters,
              mode: (e.target.value as CourseMode) || undefined,
              page: 1,
            })
          }
          className="rounded-lg border border-surface-200 bg-white px-3 py-2.5 text-sm text-neutral-700 focus:border-mindeia-primary focus:outline-none focus:ring-1 focus:ring-mindeia-primary"
          style={{ minHeight: "44px" }}
        >
          <option value="">{t("courses.all_modes")}</option>
          <option value="free">{t("courses.mode.free")}</option>
          <option value="rag">{t("courses.mode.rag")}</option>
          <option value="hybrid">{t("courses.mode.hybrid")}</option>
        </select>
      </div>

      {/* Reset button */}
      {hasActiveFilters && (
        <button
          type="button"
          onClick={resetFilters}
          className="flex items-center gap-1.5 rounded-lg border border-surface-200 bg-white px-3 text-sm text-neutral-600 transition-colors hover:bg-surface-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-primary"
          style={{ minHeight: "44px" }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M2 2l10 10M12 2L2 12"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          {t("courses.reset_filters")}
        </button>
      )}
    </div>
  )
}
